import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/Header';
import Toggle from '../../components/Toggle';
import { useRecoilValue } from 'recoil';
import { fetchAPIResponseSelector } from "../../store/user.atom";
import { toast } from "react-toastify";

type Delivery = {
  distance_from: number;
  distance_to: number;
  price: number;
};

const Settings: React.FC = () => {
  const fetchAPIResponse = useRecoilValue(fetchAPIResponseSelector);

  // Store status
  const [shopOpen, setShopOpen] = useState<boolean>(true);

  // Loyalty
  const [loyaltyEnabled, setLoyaltyEnabled] = useState<boolean>(true);
  const [cashbackRate, setCashbackRate] = useState<string>('');
  const [savingCashback, setSavingCashback] = useState<boolean>(false);

  // Global discount
  const [globalDiscount, setGlobalDiscount] = useState<string>('');
  const [savingDiscount, setSavingDiscount] = useState<boolean>(false);

  // Carousel
  const [carouselImages, setCarouselImages] = useState<string[]>([]);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delivery intervals
  const [deliveryData, setDeliveryData] = useState<Delivery[]>([]);
  const [upperLimit, setUpperLimit] = useState<string>('');
  const [lowerLimit, setLowerLimit] = useState<string>('');
  const [price, setPrice] = useState<string>('');

  useEffect(() => {
    const get = <T,>(path: string) => fetchAPIResponse<T>(path, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    });

    const load = async () => {
      try {
        const [shop, loyalty, cashback, discount, carousel, intervals] = await Promise.allSettled([
          get<boolean>('/settings/shop/status'),
          get<boolean>('/settings/loyalty'),
          get<number>('/settings/cashback'),
          get<number>('/settings/global_discount'),
          get<{ images: string[] }>('/settings/carousel'),
          get<Delivery[]>('/settings/delivery/intervals'),
        ]);

        if (shop.status === 'fulfilled') setShopOpen(shop.value.data);
        if (loyalty.status === 'fulfilled') setLoyaltyEnabled(loyalty.value.data);
        if (cashback.status === 'fulfilled' && cashback.value.data != null) setCashbackRate(String(cashback.value.data));
        if (discount.status === 'fulfilled' && discount.value.data != null) setGlobalDiscount(String(discount.value.data));
        if (carousel.status === 'fulfilled') setCarouselImages(carousel.value.data.images ?? []);
        if (intervals.status === 'fulfilled') setDeliveryData(intervals.value.data ?? []);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    load();
  }, [fetchAPIResponse]);

  // ---- Store status ----
  const toggleShop = async (value: boolean) => {
    const previous = shopOpen;
    setShopOpen(value);
    try {
      await fetchAPIResponse('/settings/shop/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ shop_status: value }),
      });
      toast.success(value ? 'Store is now open' : 'Store is now closed');
    } catch (error) {
      setShopOpen(previous);
      toast.error((error as Error).message);
    }
  };

  // ---- Loyalty ----
  const toggleLoyalty = async (value: boolean) => {
    const previous = loyaltyEnabled;
    setLoyaltyEnabled(value);
    try {
      await fetchAPIResponse('/settings/loyalty', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ loyalty_balance_enabled: value }),
      });
      toast.success(value ? 'Loyalty balance enabled' : 'Loyalty balance disabled');
    } catch (error) {
      setLoyaltyEnabled(previous);
      toast.error((error as Error).message);
    }
  };

  const saveCashback = async () => {
    const rate = Number(cashbackRate);
    if (cashbackRate.trim() === '' || isNaN(rate) || rate < 0) {
      toast.error('Enter a valid cashback rate.');
      return;
    }
    try {
      setSavingCashback(true);
      await fetchAPIResponse('/settings/cashback', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ rate }),
      });
      toast.success('Cashback rate saved.');
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSavingCashback(false);
    }
  };

  // ---- Global discount ----
  const saveGlobalDiscount = async () => {
    const value = Number(globalDiscount);
    if (globalDiscount.trim() === '' || isNaN(value) || value < 0) {
      toast.error('Enter a valid discount.');
      return;
    }
    try {
      setSavingDiscount(true);
      await fetchAPIResponse('/settings/global_discount', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ global_order_discount: value }),
      });
      toast.success('Global discount saved.');
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSavingDiscount(false);
    }
  };

  // ---- Carousel ----
  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];

    const formData = new FormData();
    formData.set('image', file, file.name);

    try {
      setUploadingImage(true);
      const response = await fetchAPIResponse<{ images: string[] }>('/settings/carousel', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData,
      });
      setCarouselImages(response.data.images ?? []);
      toast.success('Image added to carousel.');
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (image: string) => {
    if (!window.confirm('Remove this carousel image?')) return;
    try {
      const response = await fetchAPIResponse<{ images: string[] }>('/settings/carousel', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ image }),
      });
      setCarouselImages(response.data.images ?? []);
      toast.success('Image removed.');
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  // ---- Delivery intervals ----
  const handleAddDelivery = async () => {
    if (upperLimit.trim() !== '' && lowerLimit.trim() !== '' && price.trim() !== '') {
      const newDelivery: Delivery = { distance_from: Number(lowerLimit), distance_to: Number(upperLimit), price: Number(price) };
      const updatedDeliveryData = [...deliveryData, newDelivery];

      try {
        await fetchAPIResponse('/settings/delivery/intervals', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(updatedDeliveryData),
        });

        setDeliveryData(updatedDeliveryData);
        setUpperLimit('');
        setLowerLimit('');
        setPrice('');
      } catch (error) {
        toast.error((error as Error).message);
      }
    }
  };

  const handleDeleteDelivery = async (index: number) => {
    const updatedDelivery = deliveryData.filter((_, i) => i !== index);
    try {
      await fetchAPIResponse('/settings/delivery/intervals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(updatedDelivery),
      });
      setDeliveryData(updatedDelivery);
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <div className='flex flex-col items-center w-full h-full overflow-auto py-10 gap-6'>
      <Header title={'Settings'} button={false} buttonTitle={''} action={''} />

      {/* Store status */}
      <div className="zbh-card w-11/12 p-8">
        <div className="flex items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-base font-semibold text-ink">Store status</h2>
            <p className="mt-1 text-sm text-categoriesTextColor">
              When closed, customers can browse the menu but can't place orders.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`zbh-chip ${shopOpen ? 'bg-state-delivered/10 text-state-delivered' : 'bg-state-cancelled/10 text-state-cancelled'}`}>
              {shopOpen ? 'Open' : 'Closed'}
            </span>
            <Toggle checked={shopOpen} onChange={toggleShop} label="Store open" />
          </div>
        </div>
      </div>

      {/* Loyalty & cashback */}
      <div className="zbh-card w-11/12 p-8">
        <div className="flex items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-base font-semibold text-ink">Loyalty &amp; cashback</h2>
            <p className="mt-1 text-sm text-categoriesTextColor">
              Reward customers with balance they can redeem on future orders.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`zbh-chip ${loyaltyEnabled ? 'bg-state-delivered/10 text-state-delivered' : 'bg-categoriesTextColor/10 text-categoriesTextColor'}`}>
              {loyaltyEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <Toggle checked={loyaltyEnabled} onChange={toggleLoyalty} label="Loyalty enabled" />
          </div>
        </div>

        <div className="zbh-tear my-6" />

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="zbh-eyebrow mb-1.5 block">Cashback rate</label>
            <div className="relative">
              <input
                type="number"
                placeholder="e.g. 5"
                value={cashbackRate}
                onChange={(e) => setCashbackRate(e.target.value)}
                className="zbh-input w-40 pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-categoriesTextColor">%</span>
            </div>
          </div>
          <button onClick={saveCashback} disabled={savingCashback} className="zbh-btn-primary">
            {savingCashback ? 'Saving…' : 'Save rate'}
          </button>
        </div>
      </div>

      {/* Global discount */}
      <div className="zbh-card w-11/12 p-8">
        <h2 className="font-display text-base font-semibold text-ink">Global order discount</h2>
        <p className="mt-1 text-sm text-categoriesTextColor">
          Applied on top of every order. Set to 0 to turn it off.
        </p>
        <div className="mt-5 flex flex-wrap items-end gap-3">
          <div>
            <label className="zbh-eyebrow mb-1.5 block">Discount</label>
            <div className="relative">
              <input
                type="number"
                placeholder="e.g. 10"
                value={globalDiscount}
                onChange={(e) => setGlobalDiscount(e.target.value)}
                className="zbh-input w-40 pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-categoriesTextColor">%</span>
            </div>
          </div>
          <button onClick={saveGlobalDiscount} disabled={savingDiscount} className="zbh-btn-primary">
            {savingDiscount ? 'Saving…' : 'Save discount'}
          </button>
        </div>
      </div>

      {/* Carousel images */}
      <div className="zbh-card w-11/12 p-8">
        <div className="flex items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-base font-semibold text-ink">Carousel images</h2>
            <p className="mt-1 text-sm text-categoriesTextColor">
              Banner images shown at the top of the customer app's home screen.
            </p>
          </div>
          <label className={`zbh-btn-accent cursor-pointer ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`}>
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {uploadingImage ? 'Uploading…' : 'Add image'}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUploadImage} className="hidden" />
          </label>
        </div>

        <div className="zbh-tear my-6" />

        {carouselImages.length === 0 ? (
          <p className="text-sm text-categoriesTextColor">No carousel images yet — add one to feature it in the app.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {carouselImages.map((image) => (
              <div key={image} className="group relative overflow-hidden rounded-xl border border-hair">
                <img src={image} alt="Carousel banner" className="h-36 w-full object-cover" />
                <button
                  onClick={() => handleDeleteImage(image)}
                  className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-ink/70 text-white opacity-0 transition group-hover:opacity-100 hover:bg-red focus-visible:opacity-100"
                  aria-label="Remove image"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delivery intervals */}
      <div className="zbh-card w-11/12 p-8 mb-6">
        <h2 className="font-display text-base font-semibold text-ink">Delivery intervals</h2>
        <p className="mt-1 text-sm text-categoriesTextColor">Set the delivery fee for each distance band, measured from the restaurant.</p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="zbh-eyebrow mb-1.5 block">From (km)</label>
            <input type="text" placeholder="0" value={lowerLimit} onChange={(e) => setLowerLimit(e.target.value)} className="zbh-input" />
          </div>
          <div>
            <label className="zbh-eyebrow mb-1.5 block">To (km)</label>
            <input type="text" placeholder="5" value={upperLimit} onChange={(e) => setUpperLimit(e.target.value)} className="zbh-input" />
          </div>
          <div>
            <label className="zbh-eyebrow mb-1.5 block">Fee ($)</label>
            <input type="text" placeholder="3" value={price} onChange={(e) => setPrice(e.target.value)} className="zbh-input" />
          </div>
        </div>
        <button onClick={handleAddDelivery} className="zbh-btn-accent mt-4">Add interval</button>

        <div className='w-full mt-8'>
          <div className='grid grid-cols-12 gap-4 px-2'>
            <div className="zbh-th col-span-4">From (km)</div>
            <div className="zbh-th col-span-4">To (km)</div>
            <div className="zbh-th col-span-2">Fee</div>
            <div className="zbh-th col-span-2 text-right">Action</div>
          </div>
          <div className="zbh-tear mt-2" />
          {deliveryData.map((delivery, index) => (
            <div key={index} className='grid grid-cols-12 items-center gap-4 px-2 py-3 border-b border-hair last:border-0 transition hover:bg-canvas/60 rounded-lg'>
              <div className='col-span-4 zbh-figure text-sm text-ink'>{delivery.distance_from}</div>
              <div className='col-span-4 zbh-figure text-sm text-ink'>{delivery.distance_to}</div>
              <div className='col-span-2 zbh-figure text-sm font-bold text-ink'>${delivery.price}</div>
              <div className='col-span-2 flex items-center justify-end'>
                <button onClick={() => handleDeleteDelivery(index)} className="zbh-btn-danger px-4 py-1.5 text-xs">Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Settings;
